import { debounce } from "lodash";
import type Prompt from "@/models/prompt/prompt";
import usePrompts from "@/hooks/usePrompts";
import type { ColumnFilter } from "@tanstack/react-table";
import { useEffect, useMemo, useState, useCallback } from "react";
import type { User } from "@/models/users/User";
import useUsers from "@/hooks/useUsers";

interface UseAcceptedPaginationReturn {
  prompts: Prompt[];
  search: string;
  promptsLoading: boolean;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  tenantUsers: User[];
  setPageSize: (pageSize: number) => void;
  setPageIndex: (pageIndex: number) => void;
  handleSearchChange: (searchValue: string) => void;
}

const useAcceptedPagination = (): UseAcceptedPaginationReturn => {
  // Hooks
  const { useQueryPrompts } = usePrompts();
  const { useQueryAllTenantUsers } = useUsers();

  // Data state
  const [prompts, setPrompts] = useState<Prompt[]>([]);

  // Pagination state
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0); // zero-based index
  const [pageSize, setPageSize] = useState(10);

  // Sorting state
  const [sortBy] = useState<string>("updatedAt");
  const [sortAscending] = useState<boolean>(false);

  // Filtering state
  const [filters] = useState<ColumnFilter[]>([
    {
      id: "status",
      value: "Accepted",
    },
  ]);

  // Search state
  const [input, setInput] = useState(""); // For capturing immediate user input
  const [search, setSearch] = useState(""); // For the actual debounced search

  // Abort controller state
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [tenantUsers, setTenantUsers] = useState<User[]>([]);

  // Fetch data
  const fetchedPrompts = useQueryPrompts(
    {
      pageNumber: pageIndex + 1,
      pageSize,
      sortAscending,
      sortBy,
      search,
      filters,
    },
    abortController?.signal
  );
  const fetchedUsers = useQueryAllTenantUsers();

  useEffect(() => {
    if (fetchedUsers.isFetched && fetchedUsers.data) {
      setTenantUsers(fetchedUsers.data);
    }
  }, [fetchedUsers.data, fetchedUsers.isFetched]);

  // Effects
  useEffect(() => {
    if (
      fetchedPrompts.isFetched &&
      fetchedPrompts.data &&
      fetchedPrompts.data.data
    ) {
      setPrompts(fetchedPrompts.data.data);
      setPageCount(fetchedPrompts.data.totalPages);
    }
  }, [fetchedPrompts.data, fetchedPrompts.isFetched]);

  useEffect(() => {
    const debouncedFetch = debounce((searchValue: string) => {
      if (abortController) {
        abortController.abort();
      }

      const controller = new AbortController();
      setAbortController(controller);

      setPageIndex(0);
      setSearch(searchValue);
    }, 300);

    debouncedFetch(input);

    return () => {
      debouncedFetch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const handleSearchChange = useCallback((searchValue: string) => {
    setInput(searchValue);
  }, []);

  const setPageSizeCallback = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
  }, []);

  const setPageIndexCallback = useCallback((newPageIndex: number) => {
    setPageIndex(newPageIndex);
  }, []);

  return useMemo(
    () => ({
      prompts,
      promptsLoading: fetchedPrompts.isPending,
      search: input,
      pageCount,
      pageIndex,
      pageSize,
      tenantUsers,
      setPageSize: setPageSizeCallback,
      setPageIndex: setPageIndexCallback,
      handleSearchChange,
    }),
    [
      prompts,
      fetchedPrompts.isPending,
      input,
      pageCount,
      pageIndex,
      pageSize,
      tenantUsers,
      setPageSizeCallback,
      setPageIndexCallback,
      handleSearchChange,
    ]
  );
};

export default useAcceptedPagination;
